import React from 'react';
import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import type { ColumnConfig, ImportFileType } from '../types';

export function guessColumn(columns: string[], candidates: string[]): string {
  const lowerColumns = columns.map(c => c.toLowerCase());
  for (const candidate of candidates) {
    const idx = lowerColumns.findIndex(col => col.includes(candidate));
    if (idx !== -1) return columns[idx];
  }
  return '';
}

const RIDES_COLUMNS: ColumnConfig[] = [
  {
    key: 'originLat', labelKey: 'originLatitude', required: true,
    candidates: [
      'departure_latitude', 'departure_lat', 'departurelat', 'departure lat',
      'originlat', 'origin_lat', 'origin latitude', 'from_latitude',
      'fromlat', 'from_lat', 'from latitude',
      'startlat', 'start_lat', 'start latitude', 'start_latitude',
      'lat1', 'latfrom', 'lat_origin',
      'origin y', 'y1', 'yfrom', 'pickup_lat', 'pickup_latitude',
      'pickup lat', 'pickuplat', 'pickup latitude',
    ],
  },
  {
    key: 'originLng', labelKey: 'originLongitude', required: true,
    candidates: [
      'departure_longitude', 'departure_lng', 'departurelng', 'departure lon', 'departure_long', 'departure_lon',
      'originlng', 'origin_lng', 'origin longitude', 'origin_longitude', 'origin_lon',
      'fromlng', 'from_lng', 'from_lon', 'from longitude', 'from_longitude',
      'startlng', 'start_lng', 'start_lon', 'start longitude', 'start_longitude',
      'lng1', 'lon1', 'lngfrom', 'lonfrom', 'lng_origin', 'lon_origin',
      'origin x', 'x1', 'xfrom', 'pickup_lng', 'pickup_longitude',
      'pickup lon', 'pickuplng', 'pickup_long', 'pickup longitude', 'pickup_lon',
    ],
  },
  {
    key: 'destLat', labelKey: 'destinationLatitude', required: true,
    candidates: [
      'arrival_latitude', 'arrival_lat', 'arrivallat', 'arrival lat',
      'destlat', 'dest_lat', 'destinationlat', 'destination_lat', 'destination latitude', 'destination_latitude',
      'tolat', 'to_lat', 'to latitude', 'to_latitude',
      'endlat', 'end_lat', 'end latitude', 'end_latitude',
      'lat2', 'latto', 'lat_dest', 'lat_destination',
      'dest y', 'y2', 'yto', 'dropoff_lat', 'dropoff_latitude',
      'dropoff lat', 'dropofflat', 'dropoff latitude',
    ],
  },
  {
    key: 'destLng', labelKey: 'destinationLongitude', required: true,
    candidates: [
      'arrival_longitude', 'arrival_lng', 'arrivallng', 'arrival lon', 'arrival_long', 'arrival_lon',
      'destlng', 'dest_lng', 'destinationlng', 'destination_lng', 'destination longitude', 'destination_longitude', 'dest_lon',
      'tolng', 'to_lng', 'to_lon', 'to longitude', 'to_longitude',
      'endlng', 'end_lng', 'end_lon', 'end longitude', 'end_longitude',
      'lng2', 'lon2', 'lngto', 'lonto', 'lng_dest', 'lng_destination', 'lon_dest', 'lon_destination',
      'dest x', 'x2', 'xto', 'dropoff_lng', 'dropoff_longitude',
      'dropoff lon', 'dropofflng', 'dropoff_long', 'dropoff longitude', 'dropoff_lon',
    ],
  },
  {
    key: 'requestTime', labelKey: 'requestTime', required: true,
    candidates: [
      'request_time', 'requesttime', 'request time',
      'time', 'datetime', 'timestamp', 'date_time',
      'trip_time', 'triptime', 'trip time',
      'pickup_time', 'pickuptime', 'pickup time',
      'departure_time', 'departuretime', 'departure time',
      'scheduled_time', 'scheduledtime', 'scheduled time',
      'created_at', 'createdat', 'created at',
    ],
  },
  {
    key: 'fromAddress', labelKey: 'fromAddress', required: false,
    candidates: [
      'from_address', 'fromaddress', 'from address',
      'pickup_address', 'pickupaddress', 'pickup address',
      'departure_address', 'departureaddress', 'departure address',
      'origin_address', 'originaddress', 'origin address',
      'origin_name', 'originname', 'origin name',
      'pickup_name', 'pickupname', 'pickup name',
    ],
  },
  {
    key: 'toAddress', labelKey: 'toAddress', required: false,
    candidates: [
      'to_address', 'toaddress', 'to address',
      'dropoff_address', 'dropoffaddress', 'dropoff address',
      'arrival_address', 'arrivaladdress', 'arrival address',
      'destination_address', 'destinationaddress', 'destination address',
      'destination_name', 'destinationname', 'destination name',
      'dropoff_name', 'dropoffname', 'dropoff name',
    ],
  },
  {
    key: 'rideId', labelKey: 'rideId', required: false,
    candidates: [
      'external_reference_id', 'external_reference', 'external_ref_id', 'external_ref',
      'ride_id', 'rideid', 'ride id',
      'trip_id', 'tripid', 'trip id',
      'id', 'trip_reference_id', 'trip_reference',
    ],
  },
  {
    key: 'userId', labelKey: 'userId', required: false,
    candidates: [
      'external_user_reference_id', 'external_user_reference', 'external_user_ref_id', 'external_user_ref',
      'user_id', 'userid', 'user id',
      'passenger_id', 'passengerid', 'passenger id',
      'customer_id', 'customerid', 'customer id',
      'rider_id', 'riderid', 'rider id',
    ],
  },
  {
    key: 'standardSeat', labelKey: 'standardSeat', required: false,
    candidates: [
      'standard_pax', 'standardpax', 'standard pax',
      'standard_seat', 'standardseat', 'standard seat',
      'standard_passengers', 'standardpassengers', 'standard passengers',
      'seat', 'seats', 'passengers', 'pax',
      'num_passengers', 'numpassengers', 'num passengers',
      'passenger_count', 'passengercount', 'passenger count',
      'standard', 'std_seat', 'std_seats',
    ],
  },
  {
    key: 'wheelchair', labelKey: 'wheelchair', required: false,
    candidates: [
      'wheelchair_pax', 'wheelchairpax', 'wheelchair pax',
      'wheelchair', 'wheelchair_access', 'wheelchairaccess', 'wheelchair access',
      'mobility', 'mobility_aid', 'mobilityaid', 'mobility aid',
      'wheelchair_passengers', 'wheelchairpassengers', 'wheelchair passengers',
      'wc', 'wc_pax', 'wc_passengers',
      'accessible', 'accessibility', 'accessibility_needed',
    ],
  },
  {
    key: 'requestType', labelKey: 'requestType', required: false,
    candidates: [
      'request_type', 'requesttype', 'request type',
      'type', 'trip_type', 'triptype', 'trip type',
      'service_type', 'servicetype', 'service type',
      'ride_type', 'ridetype', 'ride type',
      'booking_type', 'bookingtype', 'booking type',
    ],
  },
];

const POINTS_COLUMNS: ColumnConfig[] = [
  {
    key: 'latitude', labelKey: 'latitude', required: true,
    candidates: [
      'latitude', 'lat', 'y', 'stop_lat',
      'point_lat', 'location_lat', 'geo_lat',
    ],
  },
  {
    key: 'longitude', labelKey: 'longitude', required: true,
    candidates: [
      'longitude', 'lng', 'lon', 'long', 'x', 'stop_lon',
      'point_lng', 'point_lon', 'location_lng', 'location_lon', 'geo_lng', 'geo_lon',
    ],
  },
  {
    key: 'name', labelKey: 'name', required: false,
    candidates: [
      'name', 'stop_name', 'point_name', 'label', 'title', 'location_name',
    ],
  },
  {
    key: 'description', labelKey: 'description', required: false,
    candidates: [
      'description', 'desc', 'stop_desc', 'notes', 'comment', 'details',
    ],
  },
];

const POLYGONS_COLUMNS: ColumnConfig[] = [
  {
    key: 'name', labelKey: 'name', required: false,
    candidates: ['name', 'zone_name', 'area_name', 'label', 'title', 'region'],
  },
  {
    key: 'wkt', labelKey: 'wkt', required: true,
    candidates: ['wkt', 'geometry', 'geom', 'the_geom', 'shape', 'polygon'],
  },
];

const FLEET_SHIFTS_COLUMNS: ColumnConfig[] = [
  {
    key: 'vehicleId', labelKey: 'vehicleId', required: true,
    candidates: [
      'vehicle_id', 'vehicleid', 'vehicle id', 'bus_id', 'van_id', 'fleet_id',
    ],
  },
  {
    key: 'shiftStart', labelKey: 'shiftStart', required: true,
    candidates: [
      'shift_start', 'shiftstart', 'shift start', 'start_time', 'starttime', 'start time',
    ],
  },
  {
    key: 'shiftEnd', labelKey: 'shiftEnd', required: true,
    candidates: [
      'shift_end', 'shiftend', 'shift end', 'end_time', 'endtime', 'end time',
    ],
  },
  {
    key: 'startLat', labelKey: 'startLat', required: true,
    candidates: [
      'start_lat', 'startlat', 'start lat', 'start_latitude', 'depot_lat', 'origin_lat',
    ],
  },
  {
    key: 'startLng', labelKey: 'startLng', required: true,
    candidates: [
      'start_lng', 'startlng', 'start lng', 'start_longitude', 'depot_lng', 'origin_lng',
    ],
  },
  {
    key: 'endLat', labelKey: 'endLat', required: true,
    candidates: [
      'end_lat', 'endlat', 'end lat', 'end_latitude', 'return_lat', 'destination_lat',
    ],
  },
  {
    key: 'endLng', labelKey: 'endLng', required: true,
    candidates: [
      'end_lng', 'endlng', 'end lng', 'end_longitude', 'return_lng', 'destination_lng',
    ],
  },
  {
    key: 'standardSeats', labelKey: 'standardSeats', required: true,
    candidates: [
      'standard_seats', 'standardseats', 'standard seats', 'seats', 'capacity', 'max_passengers', 'vehicle_capacity',
    ],
  },
  {
    key: 'wheelchairSeats', labelKey: 'wheelchairSeats', required: false,
    candidates: [
      'wheelchair_seats', 'wheelchairseats', 'wheelchair seats', 'wheelchair_capacity', 'accessible_seats',
    ],
  },
  {
    key: 'breakStart', labelKey: 'breakStart', required: false,
    candidates: [
      'break_start', 'breakstart', 'break start', 'lunch_start',
    ],
  },
  {
    key: 'breakEnd', labelKey: 'breakEnd', required: false,
    candidates: [
      'break_end', 'breakend', 'break end', 'lunch_end',
    ],
  },
  {
    key: 'breakLat', labelKey: 'breakLat', required: false,
    candidates: [
      'break_lat', 'breaklat', 'break lat', 'break_latitude', 'lunch_lat',
    ],
  },
  {
    key: 'breakLng', labelKey: 'breakLng', required: false,
    candidates: [
      'break_lng', 'breaklng', 'break lng', 'break_longitude', 'lunch_lng',
    ],
  },
  {
    key: 'numberOfVehicles', labelKey: 'numberOfVehicles', required: false,
    candidates: [
      'number_of_vehicles', 'numberofvehicles', 'num_vehicles', 'vehicle_count', 'count',
    ],
  },
];

export const COLUMN_CONFIGS: Record<ImportFileType, ColumnConfig[]> = {
  rides: RIDES_COLUMNS,
  points: POINTS_COLUMNS,
  polygons: POLYGONS_COLUMNS,
  fleet_shifts: FLEET_SHIFTS_COLUMNS,
  gtfs: [],
};

export const RIDES_COLUMN_MAPPING_ORDER: string[][] = [
  ['fromAddress', 'originLat', 'originLng'],
  ['toAddress', 'destLat', 'destLng'],
  ['requestTime', 'standardSeat', 'wheelchair'],
  ['rideId', 'userId', 'requestType'],
];

export const POINTS_COLUMN_MAPPING_ORDER: string[][] = [
  ['name', 'latitude', 'longitude'],
  ['description'],
];

export const POLYGONS_COLUMN_MAPPING_ORDER: string[][] = [
  ['name', 'wkt'],
];

export const FLEET_SHIFTS_COLUMN_MAPPING_ORDER: string[][] = [
  ['vehicleId', 'standardSeats', 'wheelchairSeats'],
  ['startLat', 'startLng', 'endLat', 'endLng'],
  ['shiftStart', 'shiftEnd', 'numberOfVehicles'],
  ['breakStart', 'breakEnd', 'breakLat', 'breakLng'],
];

export function getColumnMappingOrder(fileType: ImportFileType): string[][] {
  switch (fileType) {
    case 'rides': return RIDES_COLUMN_MAPPING_ORDER;
    case 'points': return POINTS_COLUMN_MAPPING_ORDER;
    case 'polygons': return POLYGONS_COLUMN_MAPPING_ORDER;
    case 'fleet_shifts': return FLEET_SHIFTS_COLUMN_MAPPING_ORDER;
    case 'gtfs': return [];
  }
}

export function getColumnMappingIcons(): Record<string, React.ReactNode> {
  return {
    fromAddress: React.createElement(Icon, { name: 'location-dot', size: 20 }),
    originLat: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    originLng: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    toAddress: React.createElement(Icon, { name: 'location-dot', size: 20 }),
    destLat: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    destLng: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    requestTime: React.createElement(Icon, { name: 'clock', size: 20 }),
    standardSeat: React.createElement(Icon, { name: 'users', size: 20 }),
    wheelchair: React.createElement(Icon, { name: 'wheelchair-move', size: 20 }),
    rideId: React.createElement(Icon, { d: ICON_FALLBACKS.ConfirmationNumberOutlined, size: 20 }),
    userId: React.createElement(Icon, { name: 'user', size: 20 }),
    requestType: React.createElement(Icon, { d: ICON_FALLBACKS.CompareArrowsOutlined, size: 20 }),
    latitude: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    longitude: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    name: React.createElement(Icon, { name: 'user-tag', size: 20 }),
    description: React.createElement(Icon, { name: 'note-sticky', size: 20 }),
    wkt: React.createElement(Icon, { name: 'pen', size: 20 }),
    vehicleId: React.createElement(Icon, { name: 'bus', size: 20 }),
    startLat: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    startLng: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    endLat: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    endLng: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    breakLat: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    breakLng: React.createElement(Icon, { d: ICON_FALLBACKS.MyLocationOutlined, size: 20 }),
    shiftStart: React.createElement(Icon, { name: 'clock', size: 20 }),
    shiftEnd: React.createElement(Icon, { name: 'clock', size: 20 }),
    standardSeats: React.createElement(Icon, { name: 'users', size: 20 }),
    wheelchairSeats: React.createElement(Icon, { name: 'wheelchair-move', size: 20 }),
    breakStart: React.createElement(Icon, { name: 'mug-hot', size: 20 }),
    breakEnd: React.createElement(Icon, { name: 'mug-hot', size: 20 }),
    numberOfVehicles: React.createElement(Icon, { name: 'bus', size: 20 }),
  };
}

export function guessColumnMappingForType(
  columns: string[],
  fileType: ImportFileType
): Record<string, string> {
  const config = COLUMN_CONFIGS[fileType];
  const mapping: Record<string, string> = {};
  for (const col of config) {
    mapping[col.key] = guessColumn(columns, col.candidates);
  }
  return mapping;
}

export function getRequiredColumns(fileType: ImportFileType): string[] {
  return COLUMN_CONFIGS[fileType]
    .filter(c => c.required)
    .map(c => c.key);
}

export function getMissingRequiredColumns(
  fileType: ImportFileType,
  columnMapping: Record<string, string>
): string[] {
  return getRequiredColumns(fileType).filter(key => !columnMapping[key]);
}
